import * as React from 'react'
import { useHistory } from 'react-router'
import { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Button from '../../ui/button'
import TextField from '../../form/TextField'
import TextAreaField from '../../form/TextAreaField'
import CheckboxField from '../../form/CheckboxField'
import ImagePreview from '../../contribution/upload/imagePreview'

import Dropzone from 'react-dropzone'
import { Form, Formik, Field, FieldArray, FieldProps, FormikProps } from 'formik'
import * as Yup from 'yup'

import { RootState } from '../../../store/rootReducer'
import {
    ContributionMaterialType,
    MaterialComment,
    SubmitContribution,
    UploadContributionMaterial
} from '../../../store/contributions/contributionsTypings'

import {
    contributionSubmit,
    addPendingMaterial,
    pendingMaterialDelete,
    fetchPendingContributions,
    deleteErrorPendingMaterial
} from '../../../store/contributions/contributionsSlice'
import axios from 'axios'
import isEmpty from 'lodash/isEmpty'
import { uploadMaterial } from '../../../api/contributions'
import paths from '../../../paths'
import './uploader.sass'
import LoginDialog from '../../informationWindow/loginDialog'

type Values = {
    title: string
    description: string
    blur_the_face: 0 | 1
    comments: MaterialComment[]
    isVideoInComments: boolean
    authorize_publication: boolean
    desired_section: 'free' | 'pro'
    confirm_ownership_and_adulthood: boolean
}

const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    // description: Yup.string().required('Description is required'),
    authorize_publication: Yup.boolean().oneOf(
        [true],
        'Please confirm that you authorize publication of these files on'
    ),
    confirm_ownership_and_adulthood: Yup.boolean().oneOf(
        [true],
        'You need confirm that you are owner and the models on your materials are adult'
    ),
    comments: Yup.array().when('isVideoInComments', {
        is: true,
        then: Yup.array().max(12, 'Maximum 12 attachments contribution'),
        otherwise: Yup.array()
            .min(4, 'Minimum 4 attachments in contribution')
            .max(12, 'Maximum 12 attachments contribution')
    })
})

const ContributionUploader: React.FunctionComponent = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const [t] = useTranslation()

    const { pendingMaterials } = useSelector((state: RootState) => state.contributions)
    const user = useSelector((state: RootState) => state.member.user)

    const formikRef = useRef<FormikProps<Values>>(null)
    const [uploadingMaterials, setUploadingMaterials] = useState([])
    const [loginDialog, setLoginDialog] = useState(false)

    const initialValues: Values = {
        title: '',
        comments: [],
        isVideoInComments: false,
        description: '',
        blur_the_face: 1,
        desired_section: 'free',
        authorize_publication: false,
        confirm_ownership_and_adulthood: false
    }

    const onFilesAdded = (files: File[]): void => {
        uploadFiles(files)
    }

    const uploadFiles = async (files: File[]): Promise<void> => {
        const promises: Promise<void>[] = []

        files.forEach((file: File) => {
            promises.push(sendRequest(file))
        })

        try {
            await Promise.all(promises)
        } catch (e) {
            throw new Error('Error uploading files')
        }
    }

    const sendRequest = (file: File): Promise<void> => {
        const params = {
            file,
            progress: 0,
            source: axios.CancelToken.source(),
            id: Math.random().toString(36).substr(2, 9)
        }

        setUploadingMaterials((prevState) => {
            return [...prevState, params]
        })

        return new Promise<void>(() => {
            uploadMaterial(params, handleUploadProgress)
                .then((res) => {
                    setUploadingMaterials((prev) => {
                        return prev.filter((material: UploadContributionMaterial) => material.id !== params.id)
                    })
                    dispatch(addPendingMaterial(res.files))
                })
                .catch((e) => {
                    if (!user.active) {
                        setLoginDialog(true)
                    } else {
                        console.log(e)
                    }
                })
        })
    }

    const handleUploadProgress = (id: string, progressEvent: ProgressEvent) => {
        setUploadingMaterials((prev) => {
            return prev.map((material: UploadContributionMaterial) => {
                if (material.id === id) {
                    material.progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
                }
                return material
            })
        })
    }

    const cancelRequest = (item: UploadContributionMaterial) => {
        item.source.cancel()
        setUploadingMaterials(
            uploadingMaterials.filter(
                (uploadingMaterial: UploadContributionMaterial) => uploadingMaterial.id !== item.id
            )
        )
    }

    const deleteMaterial = (material: ContributionMaterialType): void => {
        if (material.error) {
            dispatch(deleteErrorPendingMaterial({ id: material.id }))
        } else {
            dispatch(pendingMaterialDelete({ id: material.id }))
        }
    }

    useEffect(() => {
        dispatch(fetchPendingContributions())
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue(
                'isVideoInComments',
                pendingMaterials.some((item) =>
                    // @ts-ignore
                    item.mime_type ? item.mime_type.includes('video') : item.type.includes('video')
                )
            )

            formikRef.current.setFieldValue(
                'comments',
                pendingMaterials.map((material: ContributionMaterialType, index) =>
                    Array.isArray(formikRef.current.values.comments[index])
                        ? formikRef.current.values.comments[index]
                        : [Number(material.id), '']
                )
            )
        }
    }, [pendingMaterials])

    return (
        <div className="contributionsUploader">
            <Formik
                innerRef={formikRef}
                validateOnChange={false}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values): void => {
                    const params: SubmitContribution = {
                        title: values.title,
                        comments: values.comments,
                        description: values.description,
                        blur_the_face: values.blur_the_face,
                        desired_section: values.desired_section
                    }
                    dispatch(contributionSubmit(params))
                    history.push(paths.myProfile.path)
                }}
            >
                {(props) => {
                    return (
                        <Form>
                            {pendingMaterials.length < 12 && (
                                <Dropzone
                                    maxFiles={12 - pendingMaterials.length}
                                    multiple={true}
                                    accept={['image/*', 'video/*']}
                                    onDrop={(files: File[]) => {
                                        onFilesAdded(files)
                                    }}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div {...getRootProps()} className="dropzoneUploader">
                                            <input {...getInputProps()} />
                                            <Button label={t('Add files')} type="button" mobile />

                                            <div className="desktopBlock">
                                                {t('Drag your files here')}
                                                <Button label={t('Or add files from')} type="button" />
                                            </div>

                                            <div className="requirements">
                                                <ul className="list">
                                                    <li className="listItem">
                                                        {t('Photos in jpg format with minimum size')}
                                                    </li>
                                                    <li className="listItem">{t('Do not upload compressed files')}</li>
                                                    <li className="listItem">{t('Video in format')}</li>
                                                    <li className="listItem">
                                                        {t('Low quality photos will not be published')}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </Dropzone>
                            )}
                            {(!isEmpty(pendingMaterials) || !isEmpty(uploadingMaterials)) && (
                                <div className="filesPreview">
                                    <FieldArray name="comments">
                                        {() =>
                                            !isEmpty(pendingMaterials) &&
                                            pendingMaterials.map((material: ContributionMaterialType, index) => {
                                                return (
                                                    <div className="materialPreview" key={material.id}>
                                                        <ImagePreview
                                                            material={material}
                                                            deleteMaterial={deleteMaterial}
                                                        />
                                                        <Field name={`comments.${index}.1`}>
                                                            {({ field }: FieldProps) => {
                                                                return (
                                                                    <input
                                                                        {...field}
                                                                        type="text"
                                                                        className="description"
                                                                        value={field.value || ''}
                                                                        placeholder={t('Your comment')}
                                                                    />
                                                                )
                                                            }}
                                                        </Field>
                                                    </div>
                                                )
                                            })
                                        }
                                    </FieldArray>
                                    {!isEmpty(uploadingMaterials) &&
                                        uploadingMaterials.map((material: UploadContributionMaterial) => {
                                            return (
                                                <div className="materialPreview" key={material.id}>
                                                    <ImagePreview uploadingMaterial={material} cancel={cancelRequest} />
                                                </div>
                                            )
                                        })}
                                </div>
                            )}

                            <div className="pageSubTitle">{t('Name and describe your contribution')}</div>
                            <div className="descriptionGrid">
                                <div className="descriptionGrid__item">
                                    <Field name="title">
                                        {({ field, meta }: FieldProps) => (
                                            <TextField
                                                field={field}
                                                type="text"
                                                error={meta.touched && meta.error}
                                                placeholder={t('Title of your contribution')}
                                            />
                                        )}
                                    </Field>
                                    <Field name="description">
                                        {({ field, form }: FieldProps) => (
                                            <TextAreaField
                                                form={form}
                                                field={field}
                                                placeholder={t('Contribution description')}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className="descriptionGrid__item">
                                    <div className="helpText">
                                        <p>{t('Submit contribution first line description')}</p>
                                        <p>{t('Submit contribution second line description')}</p>
                                    </div>
                                </div>
                            </div>
                            <Field name="blur_the_face">
                                {({ field, form }: FieldProps) => (
                                    <CheckboxField form={form} field={field} label={t('Blur my face')} />
                                )}
                            </Field>

                            <Field name="confirm_ownership_and_adulthood">
                                {({ field, form }: FieldProps) => (
                                    <CheckboxField
                                        form={form}
                                        field={field}
                                        label={t('I confirm that I am owner of these submitted files')}
                                    />
                                )}
                            </Field>
                            <Field name="authorize_publication">
                                {({ field, form }: FieldProps) => (
                                    <CheckboxField
                                        form={form}
                                        field={field}
                                        label={t('I authorize the publication of these files')}
                                    />
                                )}
                            </Field>

                            <div className="form__error">
                                {t(
                                    //@ts-ignore
                                    props.errors.comments ||
                                        props.errors.confirm_ownership_and_adulthood ||
                                        props.errors.authorize_publication
                                )}
                            </div>

                            <button type="submit" className="button submit">
                                {t('Submit your contribution')}
                            </button>
                            <div className={'descriptionFreePremium'}>
                                <p>{t('Conditions of receiving free access to Premium')}</p>
                                <ul className="freeList">
                                    <li>{t('15 days free for participating in Hot section (section H)')}</li>
                                    <li>{t('15 days free for sending one video over 30 seconds long (section V)')}</li>
                                    <li>{t('30 days free for attaching a certified photo to your contribution')}</li>
                                </ul>
                                <p className={'importantInformation'}>
                                    {t('Only certified amateurs can receive free access to Premium')}
                                </p>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            <LoginDialog loginDialog={loginDialog} setLoginDialog={setLoginDialog} />
        </div>
    )
}

export default ContributionUploader
